import { type DeviceEntity } from '@solinkcorp/api-types';
import { getAccessibleAddress } from '@solinkcorp/utils';
import { type AuthLocation } from '@solinkcorp/vd-common/lib/esm/auth/authUtils';
import { useMemo } from 'react';
import { useDeviceIsLocal } from './useDeviceIsLocal';

/**
 * Returns a stable instance of an AuthLocation which only changes when the relevant
 * fields on the location change.
 */
export function useAuthDevice(device: DeviceEntity): AuthLocation;
export function useAuthDevice(device: DeviceEntity | undefined): AuthLocation | undefined;
export function useAuthDevice(device: DeviceEntity | undefined): AuthLocation | undefined {
  const isLocal = useDeviceIsLocal(device?.id);
  const accessibleAddress = device
    ? getAccessibleAddress(
        {
          deviceUrl: device.deviceUrl,
          deviceLocalUrl: device.deviceLocalUrl,
          deviceUrlV2: device.deviceUrlV2,
        },
        isLocal,
      )
    : '';
  const deviceId = device?.id;
  const deviceName = device?.name ?? '';
  const currentSoftwareVersion = device?.currentSoftwareVersion ?? '';

  return useMemo(
    () =>
      deviceId
        ? {
            id: deviceId,
            name: deviceName,
            accessibleAddress: accessibleAddress ?? '',
            currentSoftwareVersion,
          }
        : undefined,
    [deviceId, deviceName, accessibleAddress, currentSoftwareVersion],
  );
}
